import { CSSProperties, useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
const nowpayApp = new URL("../assets/images/nowpayApp.png", import.meta.url).href;
const nowpayAppAR = new URL("../assets/images/nowpayAppAR.png", import.meta.url).href;
{/**Icons */}
import person from '../../shared/assets/images/person.svg';
import paper from '../../shared/assets/images/paper.svg';
import percentage from '../../shared/assets/images/percentage.svg';
import system from '../../shared/assets/images/system.svg';
import calender from '../../shared/assets/images/calender.svg';
import clock from '../../shared/assets/images/clock.svg';
import lock from '../../shared/assets/images/lock.svg';
import discount from '../../shared/assets/images/discount.svg';
import bills from '../../shared/assets/images/bills.svg';
import money from '../../shared/assets/images/money.svg';
{/**logos */}
import axa from '../../shared/assets/images/logos/axa.svg';
import elaraby from '../../shared/assets/images/logos/elaraby.svg';
import kelloggs from '../../shared/assets/images/logos/kelloggs.svg';
import lafarge from '../../shared/assets/images/logos/lafarge.svg';
import mac from '../../shared/assets/images/logos/mac.svg';
import orange from '../../shared/assets/images/logos/orange.svg';
import pepsico from '../../shared/assets/images/logos/pepsico.svg';
import trella from '../../shared/assets/images/logos/trella.svg';
import { useLanguage } from '../context/LanguageContext';
import { fadeIn, fadeUp, slideLeft, slideRight, staggerContainer } from "../lib/motion";
import { useNavigate } from "react-router-dom";

type CountUpProps = {
  value: number;
  suffix?: string;
  duration?: number;
  decimals?: number;
  start?: boolean;
};

const CountUp = ({
  value,
  suffix = "",
  duration = 1400,
  decimals = 0,
  start = true,
}: CountUpProps) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (!start) {
      setDisplayValue(0);
      return;
    }

    let startTime: number | null = null;
    let frameId = 0;

    const animate = (timestamp: number) => {
      if (startTime === null) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const currentValue = value * progress;
      setDisplayValue(currentValue);

      if (progress < 1) {
        frameId = requestAnimationFrame(animate);
      }
    };

    frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, [value, duration, start]);

  const formatted = decimals > 0
    ? displayValue.toFixed(decimals)
    : Math.round(displayValue).toString();

  return (
    <span>
      {formatted}
      {suffix}
    </span>
  );
};

const BenefitsAndImpact = () => {
  const { translation, lang } = useLanguage()
  const navigate = useNavigate()
  const statsRef = useRef<HTMLDivElement | null>(null);
  const statsInView = useInView(statsRef, { once: true, amount: 0.3 });

  const companiesBenefits = [
    {
      text: translation.companiesBenefit1,
      image: person
    },
    {
      text: translation.companiesBenefits2,
      image: paper
    },
    {
      text: translation.companiesBenefits3,
      image: percentage
    },
    {
      text: translation.companiesBenefits4,
      image: system
    },
  ]

  const teamsBenefits = [
    {
      text: translation.reduceStress,
      image: calender
    },
    {
      text: translation.customerSupport,
      image: clock
    },
    {
      text: translation.secureTransactions,
      image: lock
    },
    {
      text: translation.teamsBenefits1,
      image: discount
    },
    {
      text: translation.teamsBenefits2,
      image: bills
    },
    {
      text: translation.cashFree,
      image: money
    },
  ]

  return (
    <main>
      <motion.div
        className="px-[1.25rem] xl:px-[7.75rem] flex flex-col items-center lg:gap-[3.25rem]"
        variants={staggerContainer(0.12, 0.08)}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.3 }}
      >
        <motion.div className="text-2xl md:text-[2.5rem] leading-[100%] font-bold text-primary text-center" variants={fadeUp}>
          {translation.whatsInIt}
        </motion.div>
        <div className="flex flex-col md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-16 xl:flex xl:flex-row items-center md:items-start">
          <motion.div className="flex flex-col gap-3 md:gap-6 pt-10 text-start w-full max-w-[18.75rem] xl:hidden" variants={staggerContainer(0.1, 0.05)}>
            <motion.div className="text-2xl md:text-[1.625rem] font-bold text-primary text-center md:text-start" variants={fadeUp}>
              {translation.teams}
            </motion.div>
            {teamsBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                className="flex gap-3.5 md:gap-[1.125rem] text-blackTwo"
                variants={fadeUp}
              >
                <img src={benefit.image} alt="" className='w-9 h-9 md:w-[3.125rem] md:h-[3.125rem] hover-scale'/>
                <div className="text-base lg:text-[1.375rem] !leading-[130%] lg:leading-[1.875rem] hover-text-rise my-auto">{benefit.text}</div>
              </motion.div>
            ))}
          </motion.div>
          <motion.div className="flex flex-col gap-3 md:gap-6 pt-10 text-start lg:text-end w-full max-w-[18.75rem]" variants={staggerContainer(0.1, 0.05)}>
            <motion.div className="text-2xl md:text-[1.625rem] font-bold text-primary text-center md:text-end" variants={fadeUp}>
              {translation.companies}
            </motion.div>
            {companiesBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                className="flex gap-3.5 md:gap-[1.125rem] text-blackTwo"
                variants={fadeUp}
              >
                <img src={benefit.image} alt="" className='w-9 h-9 md:w-[3.125rem] md:h-[3.125rem] xl:hidden hover-scale'/>
                <div className="text-base lg:text-[1.375rem] !leading-[130%] lg:leading-[1.875rem] hover-text-rise my-auto">{benefit.text}</div>
                <img src={benefit.image} alt="" className='hidden xl:block w-[3.125rem] h-[3.125rem] hover-scale'/>
              </motion.div>
            ))}
          </motion.div>
          <motion.img
            src={lang === "ar" ? nowpayAppAR : nowpayApp}
            alt="nowpay app"
            className='w-[16.5rem] md:w-[26.75rem] h-[33.5rem] md:h-[54.375rem] mt-12 md:mt-0 md:col-span-2 xl:col-auto md:justify-self-center drop-shadow-[0px_10px_15px_#004E6440] animate-float'
            variants={slideLeft}
          />
          <motion.div className="hidden xl:flex flex-col gap-3 md:gap-6 pt-10 text-start w-full max-w-[18.75rem]" variants={staggerContainer(0.1, 0.05)}>
            <motion.div className="text-2xl md:text-[1.625rem] font-bold text-primary text-center md:text-start" variants={fadeUp}>
              {translation.teams}
            </motion.div>
            {teamsBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                className="flex gap-[1.125rem] text-blackTwo"
                variants={fadeUp}
              >
                <img src={benefit.image} alt="" className='w-9 h-9 md:w-[3.125rem] md:h-[3.125rem] hover-scale'/>
                <div className="text-base lg:text-[1.375rem] !leading-[130%] lg:leading-[1.875rem] hover-text-rise my-auto">{benefit.text}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.div>
      <motion.div
        ref={statsRef}
        className='flex flex-col justify-center items-center gap-12 lg:gap-[5.625rem] w-full -mt-24 xl:-mt-[6.25rem] pt-36 pb-12 lg:pb-[6.25rem] rounded-[1.25rem] lg:rounded-[1.875rem] bg-secondary'
        variants={staggerContainer(0.12, 0.08)}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
      >
        <motion.div className='flex flex-col justify-center items-center gap-3 md:gap-12' variants={fadeIn}>
          <motion.div className='text-2xl md:text-[2.5rem] leading-[100%] font-bold text-center text-primary' variants={fadeUp}>
            {translation.nowpayEffect}
          </motion.div>
          <motion.div className='flex flex-col lg:flex-row gap-6 md:gap-14' variants={staggerContainer(0.12, 0.05)}>
            <motion.div
              className='flex flex-col gap-2.5 md:gap-5 w-[14.5rem] items-center justify-start'
              style={{ "--stagger-index": 1 } as CSSProperties}
              variants={fadeUp}
            >
              <div className='text-[2.875rem] leading-[100%] font-bold text-softGreen font-sans'>
                <CountUp value={300} suffix="K+" duration={1500} start={statsInView} />
              </div>
              <div className='text-base md:text-[1.375rem] !leading-[130%] lg:leading-[1.875rem] text-center text-blackTwo'>{translation.companiesDesc}</div>
            </motion.div>
            <motion.div
              className='flex flex-col gap-2.5 md:gap-5 w-[14.5rem] items-center justify-start'
              style={{ "--stagger-index": 2 } as CSSProperties}
              variants={fadeUp}
            >
              <div className='text-[2.875rem] leading-[100%] font-bold text-softGreen font-sans'>
                <CountUp value={1.5} suffix="B+" duration={1700} decimals={1} start={statsInView} />
              </div>
              <div className='text-base md:text-[1.375rem] !leading-[130%] lg:leading-[1.875rem] text-center text-blackTwo'>{translation.paymentsDesc}</div>
            </motion.div>
            <motion.div
              className='flex flex-col gap-2.5 md:gap-5 w-[14.5rem] items-center justify-start'
              style={{ "--stagger-index": 3 } as CSSProperties}
              variants={fadeUp}
            >
              <div className='text-[2.875rem] leading-[100%] font-bold text-softGreen font-sans'>
                <CountUp value={300} suffix="K+" duration={1500} start={statsInView} />
              </div>
              <div className='text-base md:text-[1.375rem] !leading-[130%] lg:leading-[1.875rem] text-center text-blackTwo'>{translation.peopleDesc}</div>
            </motion.div>
          </motion.div>
        </motion.div>
        <motion.div className='flex flex-col justify-center items-center gap-8 md:gap-[4.375rem] px-[1.25rem] xl:px-0' variants={staggerContainer(0.1, 0.05)}>
          <motion.div className='flex flex-col md:flex-row items-center justify-between` gap-8 lg:gap-20 md:mx-0'>
            <motion.div className='flex items-center justify-between gap-8 lg:gap-14 xl:gap-20 mx-5 md:mx-0'>
              <motion.div className="staggered-pop flex items-center justify-center" style={{ "--stagger-index": 1 } as CSSProperties} variants={fadeUp}>
                <img src={pepsico} alt="pepsico" className="hover-scale" />
              </motion.div>
              <motion.div className="staggered-pop flex items-center justify-center" style={{ "--stagger-index": 2 } as CSSProperties} variants={fadeUp}>
                <img src={orange} alt="orange" className='justify-self-end lg:justify-self-auto hover-scale' />
              </motion.div>
            </motion.div>
            <motion.div className='flex items-center justify-between gap-8 lg:gap-14 xl:gap-20 mx-6 md:mx-0'>
              <motion.div className="staggered-pop flex items-center justify-center" style={{ "--stagger-index": 3 } as CSSProperties} variants={fadeUp}>
                <img src={axa} alt="axa" className='scale-90 md:scale-100 hover-scale' />
              </motion.div>
              <motion.div className="staggered-pop flex items-center justify-center" style={{ "--stagger-index": 4 } as CSSProperties} variants={fadeUp}>
                <img src={kelloggs} alt="kelloggs" className='justify-self-end lg:justify-self-auto hover-scale' />
              </motion.div>
            </motion.div>
          </motion.div>
          <motion.div className='flex flex-col md:flex-row items-center justify-between gap-8 lg:gap-12 mx-5 md:mx-0'>
            <motion.div className='flex items-center justify-between gap-8 xl:gap-12'>
              <motion.div className="staggered-pop flex items-center justify-center" style={{ "--stagger-index": 5 } as CSSProperties} variants={fadeUp}>
                <img src={lafarge} alt="lafarge" className="hover-scale" />
              </motion.div>
              <motion.div className="staggered-pop flex items-center justify-center" style={{ "--stagger-index": 6 } as CSSProperties} variants={fadeUp}>
                <img src={mac} alt="mac" className='justify-self-end lg:justify-self-auto hover-scale' />
              </motion.div>
            </motion.div>
            <motion.div className='flex items-center justify-between gap-8 xl:gap-12 me-3 md:me-0'>
              <motion.div className="staggered-pop flex items-center justify-center" style={{ "--stagger-index": 7 } as CSSProperties} variants={fadeUp}>
                <img src={trella} alt="trella" className="scale-90 md:scale-100 hover-scale" />
              </motion.div>
              <motion.div className="staggered-pop flex items-center justify-center" style={{ "--stagger-index": 8 } as CSSProperties} variants={fadeUp}>
                <img src={elaraby} alt="elaraby" className='scale-125 md:scale-100 justify-self-end lg:justify-self-auto hover-scale' />
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.div>
        <motion.button
          className={`h-11 lg:h-[3.625rem] inline-flex items-center justify-center bg-softGreen text-white px-[3.75rem] py-0 rounded-[6.25rem] text-[1.125rem] lg:text-[1.375rem] font-semibold leading-normal hover:bg-[#66B57C] transition-colors duration-300 hover-glow ${lang === "ar" ? "font-arabic" : ""}`}
          variants={slideRight}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => navigate("/our-client")}
        >
          {translation.ourClients}
        </motion.button>
      </motion.div>
    </main>
  )
}

export default BenefitsAndImpact
